import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  ChevronLeft,
  Flame,
  Waves,
  BarChart3
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useGuestApi } from '@/hooks/useRoleBasedApi';
import UnifiedMobileFooter from '@/components/UnifiedMobileFooter';

interface Dip {
  id: number;
  location: { id: number; name: string };
  dip_date: string;
  dip_time?: string;
  duration_seconds?: number;
  duration_minutes?: number;
  duration?: number;
  temperature?: number;
  notes?: string;
  created_at: string;
  type?: 'sauna' | 'cold';
  difficulty?: number;
  started_at?: string;
}

interface Badge {
  name: string;
  display_name: string;
  description: string;
  icon: string;
  color: string;
  category?: string;
  earned_at?: string;
}

const ProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const guestApi = useGuestApi();
  
  const [dips, setDips] = useState<Dip[]>([]);
  const [badges, setBadges] = useState<Badge[]>([]);
  const [streaks, setStreaks] = useState<{ sauna: number; cold: number }>({ sauna: 0, cold: 0 });
  const [stats, setStats] = useState<{ week: number; streak: number; total: number; minutes: number }>({ week: 0, streak: 0, total: 0, minutes: 0 });
  const [loading, setLoading] = useState(true);

  // Helper function to get dip date
  const getDipDate = (dip: Dip): string | undefined => {
    return dip.dip_date || dip.created_at || dip.started_at;
  };

  // Calculate day streak (consecutive days with at least one dip)
  const calculateDayStreak = (dips: Dip[]): number => {
    if (!dips.length) return 0;
    const days = Array.from(new Set(dips.map(d => getDipDate(d)).filter(Boolean)));
    days.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    let streak = 1;
    for (let i = 1; i < days.length; i++) {
      const diff = Math.abs(new Date(days[i - 1]).getTime() - new Date(days[i]).getTime()) / (1000 * 60 * 60 * 24);
      if (diff === 1) streak++;
      else break;
    }
    return streak;
  };

  // Calculate type streak (consecutive dips of a type)
  const calculateTypeStreak = (dips: Dip[], type: 'sauna' | 'cold'): number => {
    if (!dips.length) return 0;
    const filtered = dips.filter(d => d.type === type);
    if (!filtered.length) return 0;
    const days = Array.from(new Set(filtered.map(d => getDipDate(d)).filter(Boolean)));
    days.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    let streak = 1;
    for (let i = 1; i < days.length; i++) {
      const diff = Math.abs(new Date(days[i - 1]).getTime() - new Date(days[i]).getTime()) / (1000 * 60 * 60 * 24);
      if (diff === 1) streak++;
      else break;
    }
    return streak;
  };

  // Fetch all dips
  const fetchAllDips = async () => {
    let allDips: Dip[] = [];
    let page = 1;
    let hasMore = true;
    try {
      while (hasMore) {
        const data = await guestApi.get(`/dips?page=${page}`);
        allDips = allDips.concat(data.dips);
        hasMore = data.has_more;
        page++;
      }
    } catch (error) {
      console.error('Could not fetch dips:', error);
      
      // Report specific backend issues
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          console.error('BACKEND ISSUE: Network error - backend may be down or CORS not configured');
        } else if (error.message.includes('401')) {
          console.error('BACKEND ISSUE: Authentication error - user session may be invalid');
        } else if (error.message.includes('404')) {
          console.error('BACKEND ISSUE: API endpoint not found - check if /dips endpoint exists');
        } else if (error.message.includes('500')) {
          console.error('BACKEND ISSUE: Server error - backend may have crashed');
        } else {
          console.error('BACKEND ISSUE:', error.message);
        }
      }
    }
    return allDips;
  };

  // Fetch badges
  const fetchBadges = async () => {
    try {
      const user = await guestApi.get('/auth/user', { isPublic: true });
      return user.badges || [];
    } catch (error) {
      console.error('Could not fetch badges:', error);
      return [];
    }
  };

  // Load progress data
  const loadProgressData = async () => {
    try {
      setLoading(true);

      // Always use real API calls, no more mock data
      const [dipsData, badgesData] = await Promise.all([
        fetchAllDips(),
        fetchBadges()
      ]);

      setDips(dipsData);
      setBadges(badgesData);

      // Calculate stats
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const dipsThisWeek = dipsData.filter(dip => {
        const dateStr = getDipDate(dip);
        return dateStr && new Date(dateStr) >= weekAgo;
      });
      const totalMinutesThisWeek = dipsThisWeek.reduce((sum, dip) => 
        sum + (dip.duration_minutes || dip.duration || (dip.duration_seconds ? Math.round(dip.duration_seconds / 60) : 0)), 0);

      setStats({
        week: dipsThisWeek.length,
        streak: calculateDayStreak(dipsData),
        total: dipsData.length,
        minutes: totalMinutesThisWeek,
      });

      setStreaks({
        sauna: calculateTypeStreak(dipsData, 'sauna'),
        cold: calculateTypeStreak(dipsData, 'cold'),
      });

    } catch (error) {
      console.error('Failed to load progress data:', error);
      
      // Report specific backend issues
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          console.error('BACKEND ISSUE: Network error - backend may be down or CORS not configured');
        } else if (error.message.includes('401')) {
          console.error('BACKEND ISSUE: Authentication error - user session may be invalid');
        } else if (error.message.includes('404')) {
          console.error('BACKEND ISSUE: API endpoint not found - check if /dips and /badges endpoints exist');
        } else if (error.message.includes('500')) {
          console.error('BACKEND ISSUE: Server error - backend may have crashed');
        } else {
          console.error('BACKEND ISSUE:', error.message);
        }
      }
      
      // Set empty states for failed API calls
      setDips([]);
      setBadges([]);
      setStats({ week: 0, streak: 0, total: 0, minutes: 0 });
      setStreaks({ sauna: 0, cold: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadProgressData();
    }
  }, [user]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Progress | SaunaDip</title>
          <meta name="description" content="Track your sauna and cold plunge progress" />
        </Helmet>
        <div className="bg-gray-50">
          <div className="flex items-center justify-center min-h-screen">
            <LoadingSpinner size="xl" text="Loading progress..." />
          </div>
        </div>
        <UnifiedMobileFooter />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Progress | SaunaDip</title>
        <meta name="description" content="Track your sauna and cold plunge progress" />
      </Helmet>
      
      <div className="bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">Progress</h1>
          </div>
        </div>

        <div className="p-4">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 flex flex-col items-center">
              <h2 className="text-xl font-bold text-center mb-1">Progress Overview</h2>
              <div className="text-lg font-semibold text-gray-900 text-center mb-1 mt-2">Your Journey</div>
              <div className="text-gray-500 text-base text-center mb-6">Track your ritual progress</div>
              
              {/* Streak Cards */}
              <div className="flex w-full justify-between gap-4 mb-6">
                <div className="flex-1 flex flex-col items-center border-2 border-sauna-blue bg-[#E6F0FA] rounded-xl py-4">
                  <Waves className="w-6 h-6 text-sauna-blue mb-1" />
                  <span className="text-2xl font-bold text-sauna-blue">{streaks.cold}</span>
                  <span className="text-xs text-gray-700 mt-1">Cold Plunges</span>
                </div>
                <div className="flex-1 flex flex-col items-center border-2 border-sauna-orange bg-[#FFE6DB] rounded-xl py-4">
                  <Flame className="w-6 h-6 text-sauna-orange mb-1" />
                  <span className="text-2xl font-bold text-sauna-orange">{streaks.sauna}</span>
                  <span className="text-xs text-gray-700 mt-1">Sauna Sessions</span>
                </div>
              </div>
              
              {/* Badge Grid */}
              <div className="w-full mb-6">
                <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span role='img' aria-label='medal'>🏅</span> 
                  Earned Badges ({badges.length})
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {badges.length === 0 && (
                    <div className="text-xs text-gray-400 col-span-2 text-center">No badges yet</div>
                  )}
                  {badges.map(badge => (
                    <div key={badge.name} className="flex flex-col items-center bg-[#F8F8F8] rounded-xl p-3 shadow text-center">
                      <span className="text-2xl mb-1">{badge.icon}</span>
                      <span className="font-semibold text-xs text-gray-900">{badge.display_name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Week Stats Card */}
              <div className="w-full bg-gradient-to-r from-[#FFE6DB] via-[#E6F0FA] to-[#E6FAE6] rounded-2xl shadow p-6 flex justify-between items-center">
                <div className="flex flex-col items-center flex-1">
                  <span className="text-2xl font-bold text-sauna-orange">{stats.week}</span>
                  <span className="text-xs text-gray-700 mt-1">Total Sessions</span>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-2xl font-bold text-sauna-blue">{stats.minutes}</span>
                  <span className="text-xs text-gray-700 mt-1">Minutes</span>
                </div>
                <div className="flex flex-col items-center flex-1">
                  <span className="text-2xl font-bold text-green-600">{stats.streak}</span>
                  <span className="text-xs text-gray-700 mt-1">Day Streak</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UnifiedMobileFooter />
    </>
  );
};

export default ProgressPage; 